import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/", label: t("nav.category") },
    { path: "/", label: t("nav.hot") },
    { path: "/about", label: t("nav.about") },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center">
              <i className={`ri-fire-fill text-xl md:text-2xl ${isScrolled ? "text-primary-500" : "text-white"}`}></i>
            </div>
            <span
              className={`font-heading font-bold text-lg md:text-xl transition-colors duration-300 ${
                isScrolled ? "text-foreground-900" : "text-white"
              }`}
            >
              51暗网吃瓜
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isActive(link.path)
                    ? isScrolled
                      ? "text-primary-500"
                      : "text-white font-semibold"
                    : isScrolled
                      ? "text-foreground-600 hover:text-foreground-900"
                      : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="#cta-download"
              className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-sm"></i>
              </div>
              {t("nav.download")}
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className={`w-9 h-9 flex items-center justify-center`}>
              <i
                className={`text-xl transition-colors duration-300 ${
                  isScrolled ? "text-foreground-900" : "text-white"
                } ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}
              ></i>
            </div>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? "bg-primary-50 text-primary-600"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="#cta-download"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 cursor-pointer whitespace-nowrap text-center mt-1"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-sm"></i>
              </div>
              {t("nav.download")}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}